/**
 * The powers of the realm.
 *
 * A nation is a faction with a capital, a treasury and a temperament. The
 * temperament is three numbers, deliberately — an AI whose whole personality
 * fits on one line is an AI a player can learn to read, and being readable is
 * the entire job of a strategic opponent.
 *
 * There are twenty-four of them. Eight are hand-authored and are the powers the
 * setting was written around; the other sixteen are generated from the same
 * fixed seed the province table is, each cut from a named *doctrine* — raider,
 * bastion, merchant, zealot, opportunist, hermit, warlord, steward — so that two
 * dozen crowns are two dozen temperaments rather than eight repainted. See the
 * `DOCTRINES` table in `realm.ts`; that is where a new kind of neighbour is
 * added, and adding one costs six lines.
 *
 * The eight authored powers are always indices 0..7, which matters: the player's
 * seat is chosen by `nationForFaction`, and it must be the authored crown for
 * that faction rather than whichever generated one happens to share its roster.
 */

import { faction } from './factions.ts';
import { provinceIndex } from './provinces.ts';
import { REALM } from './realm.ts';

export type { NationDef } from './realm.ts';
import type { NationDef } from './realm.ts';

export const NATIONS: NationDef[] = REALM.nations;

const BY_ID = new Map<string, number>();
NATIONS.forEach((def, i) => BY_ID.set(def.id, i));

export function nationIndex(id: string): number {
  const i = BY_ID.get(id);
  return i === undefined ? -1 : i;
}

export function nationDef(i: number): NationDef {
  return NATIONS[i] ?? NATIONS[0];
}

/** The nation a given playable faction starts as, or -1 if it has no seat. */
export function nationForFaction(factionId: string): number {
  return NATIONS.findIndex((n) => n.factionId === factionId);
}

/**
 * Capitals, resolved once.
 *
 * `nationCapital` is called inside the AI's per-province scoring loop, which at
 * this board size runs a few million times a campaign. It was a string lookup
 * through two maps.
 */
const CAPITAL_OF: number[] = NATIONS.map((n) => provinceIndex(n.capital));

export function nationCapital(i: number): number {
  return CAPITAL_OF[i] ?? -1;
}

/**
 * Colours come from the faction unless the nation has had to override it.
 *
 * Every generated nation overrides it, and has to: there are ten factions and
 * twenty-four seats, so most factions seat more than one crown, and on the one
 * screen where every power is visible at once two crowns of the same colour are
 * one crown. The hues are assigned in `realm.ts` so that no two nations whose
 * ground actually touches are close on the wheel.
 */
const ACCENT: number[] = NATIONS.map((def) => def.banner ?? faction(def.factionId).accent);

export function nationAccent(i: number): number {
  return ACCENT[i] ?? ACCENT[0];
}

export const NATION_COUNT = NATIONS.length;
